import styled from "styled-components";
import global from "../../global";

interface SidebarProps {
  extendedMode: boolean;
  isMenuOpen?: boolean;
}

export const Container = styled.div<SidebarProps>`
  background-color: var(--background);
  border-right: 1px solid var(--light-gray);
  z-index: 120;

  position: fixed;
  top: 60px;
  bottom: 0;
  left: 0;
  
  width: ${({ extendedMode }) => (extendedMode ? "260px" : "71px")};
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  display: flex;
  flex-direction: column;

  @media (max-width: ${global.responsive.small.width.value}${global.responsive
      .small.width.unit}) {
    width: 260px; /* Always expanded on mobile */
    transform: ${({ isMenuOpen }) => (isMenuOpen ? "translateX(0)" : "translateX(-100%)")};
  }
`;

export const Overlay = styled.div<{ isMenuOpen: boolean }>`
  display: none;
  
  @media (max-width: ${global.responsive.small.width.value}${global.responsive.small.width.unit}) {
    display: ${({ isMenuOpen }) => (isMenuOpen ? "block" : "none")};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: transparent;
    z-index: 110;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 15px;
  height: 80px;
  border-bottom: 1px solid var(--light-gray);
  
  img {
    transition: opacity 0.3s, transform 0.3s;
  }
`;

export const ToggleButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--gray);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    color: var(--fixit);
    background-color: var(--light-gray);
  }

  @media (max-width: ${global.responsive.small.width.value}${global.responsive.small.width.unit}) {
    display: none; /* Hide toggle button on mobile since it is always expanded */
  }
`;

interface CollapseButtonProps {
  extendedMode: boolean;
}

export const SidebarCollapseButton = styled.button<CollapseButtonProps>`
  position: absolute;
  right: -20px; 
  top: calc(50% - 28px); 
  transform: translateY(-50%);
  width: 20px; 
  height: 28px; 
  border-radius: 0 12px 12px 0; 
  
  /* Fundo lavado: #9835D1 (RGB: 152, 53, 209) com 8% de opacidade */
  background-color: rgba(152, 53, 209, 0.08); 
  
  border: 1px solid var(--fixit);
  border-left: none;
  color: var(--fixit);
  
  box-shadow: none;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  z-index: 125;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(152, 53, 209, 0.15);
  }

  @media (max-width: ${global.responsive.small.width.value}${global.responsive.small.width.unit}) {
    display: none;
  }
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding-top: 15px;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Esconde a barra de rolagem, mas mantém a funcionalidade do scroll */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`;

export const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const FooterContainer = styled.div`
  padding: 15px;
  border-top: 1px solid var(--light-gray);
  display: flex;
  justify-content: center;
`;

/* Accordion Component Styles */
interface MenuItemProps {
  isActive?: boolean;
  extendedMode: boolean;
}

export const MenuItemContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  a {
    text-decoration: none;
  }
`;

export const MenuButton = styled.button<MenuItemProps>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background-color: ${({ isActive }) => (isActive ? "rgba(152, 53, 209, 0.05)" : "transparent")};
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  border-radius: 0 20px 20px 0; /* Left side flat, right side heavily rounded */
  margin: 4px 12px 4px 0;    /* Margin on right only, flat against left edge */
  width: calc(100% - 12px); /* Compensate the margin */
  overflow: hidden; /* Important for fluid animation without breaking layout */

  /* The left-side active indicator bar, rounded like the reference image */
  &::before {
    content: '';
    position: absolute;
    left: 0px; 
    top: 50%;
    transform: translateY(-50%);
    height: 65%;
    width: 3px;
    background-color: var(--fixit);
    border-radius: 0 6px 6px 0; /* Flat on left, rounded on right */
    opacity: ${({ isActive }) => (isActive ? 1 : 0)};
    transition: opacity 0.2s, height 0.2s;
  }

  @media (max-width: ${global.responsive.small.width.value}${global.responsive.small.width.unit}) {
    justify-content: space-between;
    padding: 12px 20px;
    
    .content span {
      opacity: 1 !important;
    }
  }

  &:hover {
    background-color: rgba(152, 53, 209, 0.05);
    
    .content svg, 
    .content span {
      color: var(--fixit);
    }

    .arrow {
      color: var(--fixit);
    }
  }

  /* Left side content (icon + text) */
  .content {
    display: flex;
    align-items: center;
    gap: 15px;

    svg {
      color: ${({ isActive }) => (isActive ? "var(--fixit)" : "var(--gray)")};
      min-width: 24px;
      transition: color 0.2s;
    }

    span {
      font-size: 14px;
      font-weight: ${({ isActive }) => (isActive ? "600" : "500")};
      color: ${({ isActive }) => (isActive ? "var(--fixit)" : "var(--gray)")};
      white-space: nowrap;
      transition: color 0.2s, opacity 0.3s;
      opacity: ${({ extendedMode }) => (extendedMode ? 1 : 0)};
    }
  }

  /* Right arrow for submenus */
  .arrow {
    color: var(--gray);
    transition: transform 0.3s ease, opacity 0.3s ease;
    opacity: ${({ extendedMode }) => (extendedMode ? 1 : 0)};
  }
`;

interface SubMenuProps {
  isOpen: boolean;
  contentHeight: number;
}

export const SubMenuWrapper = styled.div<SubMenuProps>`
  overflow: hidden;
  height: ${({ isOpen, contentHeight }) => (isOpen ? `${contentHeight}px` : "0px")};
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left: 32px;
  border-left: 1px solid var(--light-gray);
  margin-right: 8px; /* Added to keep right alignment with parent's margin */
`;

export const SubMenuItem = styled.div<{ isActive?: boolean }>`
  a {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 12px 20px 12px 14px;
    text-decoration: none;
    background-color: ${({ isActive }) => (isActive ? "rgba(152, 53, 209, 0.05)" : "transparent")};
    color: ${({ isActive }) => (isActive ? "var(--fixit)" : "var(--gray)")};
    font-size: 14px;
    font-weight: ${({ isActive }) => (isActive ? "600" : "500")};
    transition: all 0.2s;
    position: relative;
    border-radius: 0 20px 20px 0; /* Left side flat against border, right side heavily rounded */
    margin: 4px 0;

    /* The left-side active indicator bar, rounded like the reference image */
    &::before {
      content: '';
      position: absolute;
      left: -2px; /* Centered perfectly over the 1px gray border */
      top: 50%;
      transform: translateY(-50%);
      height: 65%;
      width: 3px;
      background-color: var(--fixit);
      border-radius: 6px; /* Rounded on all sides to look like a timeline node */
      opacity: ${({ isActive }) => (isActive ? 1 : 0)};
      transition: opacity 0.2s;
    }

    &:hover {
      color: var(--fixit);
      background-color: rgba(152, 53, 209, 0.05);
    }
  }

  .sub-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    min-width: 24px;
    transition: color 0.2s;
  }
`;
